import { JetView } from 'webix-jet'; // Importing JetView from webix-jet for creating views
import { allpayments } from 'models/allpayments'; // Importing allpayments model
import { devConfig } from 'config/config'; // Importing development configuration

/**
 * Class representing the historical cash flow view.
 * @extends JetView
 */
export default class tableHistoricalCashFlowView extends JetView {
    /**
     * Configures the view layout and components.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const dateFormat = webix.Date.dateToStr(this.app.config.dateFormat); // Date formatting function
        const id_partner = this.getParam('partner', true); // Getting partner ID from parameters

        // Extending the summTreeColumn filter for custom summation behavior
        webix.ui.datafilter.summTreeColumn = webix.extend(
            {
                /**
                 * Refreshes the summation display for the tree column.
                 * @param {Object} master - The master view.
                 * @param {Object} node - The node to refresh.
                 * @param {Object} value - The value object containing columnId.
                 */
                refresh: function (master, node, value) {
                    var result = 0; // Variable to hold the summation result
                    var resultFormat = 0; // Variable for formatted result
                    master.data.each(function (obj) {
                        if (obj.$level == 1) result += obj[value.columnId]; // Summing values at level 1
                    });
                    // Formatting the result based on the columnId
                    if (value.columnId === 'CantidadTickets') {
                        resultFormat = webix.i18n.intFormat(result);
                    } else {
                        resultFormat = webix.i18n.priceFormat(result);
                    }
                    node.firstChild.innerHTML = resultFormat; // Updating the node with formatted result
                },
            },
            webix.ui.datafilter.summColumn, // Extending from the existing summColumn filter
        );

        // Returning the layout configuration
        return {
            rows: [
                {
                    id: 'preloader', // ID for the preloader
                    cols: [
                        {
                            view: 'treetable', // Defining a tree table view
                            id: 'treetable', // ID for the tree table
                            css: 'webix_header_border webix_data_border webix_footer_border', // CSS classes for styling
                            scheme: {
                                $group: {
                                    by: 'Punto', // Grouping by 'Punto'
                                    css: 'Total-bold', // CSS class for group header
                                    footer: {
                                        // Footer configuration for grouped columns
                                        MMoneda: ['MMoneda', 'sum'],
                                        Fecha: ["<span style='font-weight:bold;'>Total:</span>", 'string'],
                                        CantidadTickets: ['CantidadTickets', 'sum'],
                                        ValorEntradasEfectivo: ['ValorEntradasEfectivo', 'sum'],
                                        ValorEntradasBonoTC: ['ValorEntradasBonoTC', 'sum'],
                                        ValorEntradasTraslados: ['ValorEntradasTraslados', 'sum'],
                                        ValorEntradasRecargas: ['ValorEntradasRecargas', 'sum'],
                                        ValorSalidasEfectivo: ['ValorSalidasEfectivo', 'sum'],
                                        ValorSalidasTraslados: ['ValorSalidasTraslados', 'sum'],
                                        ValorSalidasNotasRetiro: ['ValorSalidasNotasRetiro', 'sum'],
                                        Saldo: ['Saldo', 'sum'],
                                    },
                                    map: {
                                        // Mapping for grouped columns
                                        MMoneda: ['MMoneda', 'sum'],
                                        CantidadTickets: ['CantidadTickets', 'sum'],
                                        ValorEntradasEfectivo: ['ValorEntradasEfectivo', 'sum'],
                                        ValorEntradasBonoTC: ['ValorEntradasBonoTC', 'sum'],
                                        ValorEntradasTraslados: ['ValorEntradasTraslados', 'sum'],
                                        ValorEntradasRecargas: ['ValorEntradasRecargas', 'sum'],
                                        ValorSalidasEfectivo: ['ValorSalidasEfectivo', 'sum'],
                                        ValorSalidasTraslados: ['ValorSalidasTraslados', 'sum'],
                                        ValorSalidasNotasRetiro: ['ValorSalidasNotasRetiro', 'sum'],
                                        Saldo: ['Saldo', 'sum'],
                                    },
                                },
                            },
                            bready: function () {
                                this.openAll(); // Expanding all rows on ready
                            },
                            footer: true, // Enabling footer
                            scrollY: true, // Enabling vertical scrolling
                            height: screen.width < 820 ? 400 : 0, // Setting height based on screen width
                            columns: [
                                {
                                    id: 'Fecha', // Column ID for date
                                    header: _('Fecha'), // Column header with localization
                                    width: 250, // Column width
                                    template: function (obj, common) {
                                        if (obj.$group) return common.treetable(obj, common) + obj.value; // Returning group header
                                        return obj.Fecha; // Returning date value
                                    },
                                    footer: _('Total:'), // Footer label
                                },
                                {
                                    id: 'Moneda', // Column ID for currency
                                    header: _('Moneda'), // Column header with localization
                                    width: 100, // Column width
                                },
                                {
                                    id: 'CantidadTickets', // Column ID for ticket count
                                    header: _('Cantidad Tickets'), // Column header with localization
                                    footer: { content: 'summTreeColumn' }, // Footer content using custom filter
                                    format: webix.i18n.intFormat, // Formatting function for integers
                                    width: 150, // Column width
                                },
                                {
                                    id: 'ValorEntradasEfectivo', // Column ID for cash entry value
                                    header: _('Valor Entradas Efectivo'), // Column header with localization
                                    footer: { content: 'summTreeColumn' }, // Footer content using custom filter
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 180, // Column width
                                },
                                {
                                    id: 'ValorEntradasBonoTC', // Column ID for bonus/TC entry value
                                    header: _('Valor Entradas Bono / T.C.'), // Column header with localization
                                    footer: { content: 'summTreeColumn' }, // Footer content using custom filter
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 190, // Column width
                                },
                                {
                                    id: 'ValorEntradasTraslados', // Column ID for transfer entry value
                                    header: _('Valor Entradas Traslados'), // Column header with localization
                                    footer: { content: 'summTreeColumn' }, // Footer content using custom filter
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 190, // Column width
                                },
                                {
                                    id: 'ValorEntradasRecargas', // Column ID for recharge entry value
                                    header: _('Valor Entradas Recargas'), // Column header with localization
                                    footer: { content: 'summTreeColumn' }, // Footer content using custom filter
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 190, // Column width
                                },
                                {
                                    id: 'ValorSalidasEfectivo', // Column ID for cash exit value
                                    header: _('Valor Salidas Efectivo'), // Column header with localization
                                    footer: { content: 'summTreeColumn' }, // Footer content using custom filter
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 170, // Column width
                                },
                                {
                                    id: 'ValorSalidasTraslados', // Column ID for transfer exit value
                                    header: _('Valor Salidas Traslados'), // Column header with localization
                                    footer: { content: 'summTreeColumn' }, // Footer content using custom filter
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 180, // Column width
                                },
                                {
                                    id: 'ValorSalidasNotasRetiro', // Column ID for withdrawal note value
                                    header: _('Valor Salidas Notas de Retiro'), // Column header with localization
                                    footer: { content: 'summTreeColumn' }, // Footer content using custom filter
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 160, // Column width
                                },
                                {
                                    id: 'Saldo', // Column ID for balance
                                    header: _('Saldo'), // Column header with localization
                                    footer: { content: 'summTreeColumn' }, // Footer content using custom filter
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 150, // Column width
                                },
                            ],
                            ready: function () {
                                if (!this.count()) {
                                    webix.extend(this, webix.OverlayBox); // Extending with overlay functionality
                                    this.showOverlay(
                                        "<div class='no-data' style='margin:75px; font-size:18px;'>" +
                                            _('No hay datos') + // Displaying no data message
                                            '</div>',
                                    );
                                }
                            },
                            autowidth: false, // Disabling auto width
                            math: true, // Enabling math operations
                            datafetch: 10, // Number of data items to fetch
                            pager: 'pagerA', // ID for the pager component
                        },
                    ],
                },
            ],
        };
    }

    /**
     * Initializes the view and sets up event listeners.
     * @param {Object} grid - The grid component to initialize.
     */
    init(grid) {
        const id_partner = this.getParam('partner', true); // Getting partner ID from parameters
        grid = $$('treetable'); // Getting the tree table instance
        var url =
            devConfig.geturlApi() + // Constructing the API URL
            'es/Financial/GetHistoricalCashFlow?Partner=' +
            id_partner +
            '&';

        // Event listener for filtering historical cash flow data
        this.on(this.app, 'historicalCashFlow:filter', (filterData) => {
            var object = Object.entries(filterData); // Converting filter data to entries
            var string1 = ''; // String to hold query parameters
            for (var i = 0; i < object.length; i++) {
                if (typeof object[i][1] !== 'object' && object[i][1] !== '') {
                    if (object[i][0] == 'Datepicker') {
                        var date = object[i][1].split(' - '); // Splitting date range
                        console.log(date); // Logging date range
                        string1 = string1 + '&dateFrom=' + date[0]; // Adding dateFrom parameter
                        string1 = string1 + '&dateTo=' + date[1]; // Adding dateTo parameter
                    } else {
                        if (object[i][0] != 'Date') {
                            string1 = string1 + '&' + object[i][0] + '=' + object[i][1]; // Adding other parameters
                        } else {
                            if (object[i][0] == 'Date') {
                                var date = object[i][1].replace(' 00:00:00', ''); // Formatting date
                                string1 = string1 + '&dateFrom=' + date; // Adding dateFrom parameter
                                string1 = string1 + '&dateTo=' + date; // Adding dateTo parameter
                            }
                        }
                    }
                } else {
                    if (typeof object[i][1] === 'object') {
                        var object2 = Object.entries(object[i][1]); // Converting nested object to entries
                        for (var j = 0; j < object2.length; j++) {
                            if (object2[j][1] !== null) {
                                if (object2[j][0] === 'start') {
                                    var d = new Date(object2[j][1]); // Creating date object for start
                                    var dateFrom =
                                        'dateFrom=' +
                                        d.getFullYear() +
                                        ':' +
                                        ('00' + (d.getMonth() + 1)).slice(-2) +
                                        ':' +
                                        ('00' + d.getDate()).slice(-2) +
                                        ' ' +
                                        ('00' + d.getHours()).slice(-2) +
                                        ':' +
                                        ('00' + d.getMinutes()).slice(-2) +
                                        ':' +
                                        ('00' + d.getSeconds()).slice(-2);
                                    string1 = string1 + '&' + dateFrom; // Adding dateFrom parameter
                                } else {
                                    if (object2[j][0] === 'end') {
                                        var d = new Date(object2[j][1]); // Creating date object for end
                                        var dateTo =
                                            'dateTo=' +
                                            d.getFullYear() +
                                            ':' +
                                            ('00' + (d.getMonth() + 1)).slice(-2) +
                                            ':' +
                                            ('00' + d.getDate()).slice(-2) +
                                            ' ' +
                                            ('00' + d.getHours()).slice(-2) +
                                            ':' +
                                            ('00' + d.getMinutes()).slice(-2) +
                                            ':' +
                                            ('00' + d.getSeconds()).slice(-2);
                                        string1 = string1 + '&' + dateTo; // Adding dateTo parameter
                                    }
                                }
                            }
                        }
                    }
                }
            }
            string1 = string1 + '&Partner=' + id_partner; // Adding partner ID to parameters
            grid.clearAll(); // Clearing existing grid data
            grid.load(url + '?start=0&count=' + grid.data.$pagesize + string1); // Loading new data into the grid
        });

        // Event listener for item click in the grid
        grid.attachEvent('onItemClick', function (id, e, node) {
            var item = this.getItem(id); // Getting the clicked item
            this.$scope.app.callEvent('user:id', [item.Id]); // Triggering user ID event
        });

        // Event listener for player selection
        this.on(this.app, 'player:select', (Id) => {
            grid.load(
                url + '?start=0&count=' + grid.data.$pagesize + '&PlayerId=' + Id, // Loading data based on selected player ID
            );
        });
    }
}

// FILE DOCUMENTED
